import cn from "classnames";
import { Field, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import iPaperPlane from "../../../../assets/icons/paper-plane.svg";
import iCheck from "../../../../assets/icons/check.svg";
import { userValidationSchema } from "../../../../const/userValidationSchema";
import { TContactForm } from "../../../../types/types";
import ButtonFile from "../../button-file/button-file";
import { postContactApi } from "../../../../api/api";
import privacyPolicyPdf from "../../../../assets/files/footer-form/policy-privacy.pdf";
import "./footer-form.sass";

const FooterForm = () => {
  const [isDescriptionOpen, setDescriptionOpen] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const submitControllerRef = useRef<AbortController | null>(null);

  const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === "AbortError";

  useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);

  const onSendForm = (values: TContactForm) => {
    if (isSubmitting || isSubmitted) {
      return;
    }

    const formData = new FormData();
    formData.append("fullName", values.fullName);
    formData.append("company", values.company);
    formData.append("contact", values.contact);
    formData.append("description", values.description);
    formData.append("consent", String(true));
    formData.append("link", values.link);

    if (values.file) {
      formData.append("file", values.file);
    }

    setSubmitting(true);
    setSubmitError("");
    submitControllerRef.current?.abort();
    const controller = new AbortController();
    submitControllerRef.current = controller;

    postContactApi(formData, { signal: controller.signal })
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }
        setSubmitted(true);
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setSubmitError(
          error instanceof Error && error.message.trim() !== ""
            ? error.message
            : "Не удалось отправить форму. Попробуйте еще раз.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSubmitting(false);
        }
      });
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikProps<TContactForm>["setFieldValue"],
  ) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      setFieldValue("file", file);
      setFieldValue("link", file.name);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="footer-form">
      <div className="footer-form__title">Давайте знакомиться</div>

      <Formik<TContactForm>
        initialValues={{
          fullName: "",
          company: "",
          contact: "",
          link: "",
          description: "",
          consent: false,
          file: null,
        }}
        onSubmit={(value: TContactForm) => {
          onSendForm(value);
        }}
        validationSchema={userValidationSchema}
      >
        {({
          values,
          dirty,
          errors,
          isValid,
          touched,
          setFieldValue,
          handleSubmit,
        }) => (
          <form className="footer-form__form" onSubmit={handleSubmit}>
            <div className="footer-form__form-block">
              <Field
                type="text"
                name="fullName"
                className={cn("input input_width50", {
                  error: errors.fullName && "error",
                })}
                placeholder="Ваше имя и фамилия"
              />

              <Field
                type="text"
                name="company"
                className="input input_width50"
                placeholder="Компания"
              />
            </div>

            {errors.fullName && touched.fullName && (
              <div className="error footer-form__error">{errors.fullName}</div>
            )}

            <Field
              type="text"
              name="contact"
              className={cn("input", {
                error: errors.contact && "error",
              })}
              placeholder="Телеграм или почта"
            />

            {errors.contact && touched.contact && (
              <div className="error footer-form__error">{errors.contact}</div>
            )}

            <div className="file-input">
              <Field
                type="text"
                name="link"
                className="input"
                placeholder="Оставьте ссылку или загрузите файл"
              />

              <ButtonFile onClick={handleButtonClick} />

              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => handleFileChange(e, setFieldValue)}
                style={{ display: "none" }}
              />
            </div>

            {values.file instanceof File && (
              <div className="footer-form__file-info">
                Загружен файл: {values.file.name}
              </div>
            )}

            <Field
              type="textarea"
              name="description"
              as="textarea"
              className={cn("input", { _open: isDescriptionOpen })}
              placeholder="Расскажите коротко о задаче"
              onFocus={() => setDescriptionOpen(true)}
              onBlur={() => setDescriptionOpen(false)}
            />

            {errors.description && touched.description && (
              <div className="error footer-form__error">
                {errors.description}
              </div>
            )}

            <div className="footer-form__button-block">
              <div className="footer-form__text">
                Нажимая на кнопку, вы подтверждаете ознакомление с{" "}
                <a
                  href={privacyPolicyPdf}
                  target="_blank"
                  rel="noreferrer"
                  className="footer-form__policy-link"
                >
                  политикой конфиденциальности
                </a>{" "}
                и даете согласие на обработку персональных данных.
              </div>

              <div
                className={cn("footer-form__button", {
                  "footer-form__button_disabled": isSubmitting && !isSubmitted,
                  "footer-form__button_sent": isSubmitted,
                })}
                onClick={() => {
                  handleSubmit();
                }}
              >
                {isSubmitted ? "отправлено" : isSubmitting ? "отправка..." : "отправить"}
                {isSubmitted ? (
                  <img src={iCheck} className="footer-form__img" alt="" />
                ) : (
                  <img src={iPaperPlane} className="footer-form__img" alt="" />
                )}
              </div>
            </div>
            {submitError && <div className="error footer-form__error">{submitError}</div>}
          </form>
        )}
      </Formik>
    </div>
  );
};

export default FooterForm;
